import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';

let nextId = 0;

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [LoadingSpinnerComponent, DecimalPipe],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
})
export class PaginationComponent {
  @Output() onNext = new EventEmitter();
  @Output() onPrev = new EventEmitter();
  @Output() changePage = new EventEmitter();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Input() totalPages = 1;
  @Input() currentPage = 0;
  @Input() loading = false;
  @Input() totalItems?: number;
  @Input() pageSize?: number;

  readonly pageSizeOptions = [10, 15, 20, 30, 40, 50];

  /** Unique per instance, so two paginations on a page never share ids. */
  private readonly uid = ++nextId;
  readonly sizeId = `pg-size-select-${this.uid}`;
  readonly gotoId = `pg-goto-input-${this.uid}`;

  compTotalPage = 1;
  compCurrentPage = 0;
  compPageSize = 10;

  get showingFrom(): number {
    if (!this.totalItems) return 0;
    return this.compCurrentPage * this.compPageSize + 1;
  }

  get showingTo(): number {
    if (!this.totalItems) return 0;
    return Math.min((this.compCurrentPage + 1) * this.compPageSize, this.totalItems);
  }

  get pageNumbers(): (number | '...')[] {
    const total = this.compTotalPage;
    const current = this.compCurrentPage;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i);
    }

    const pages: (number | '...')[] = [0];

    if (current > 3) pages.push('...');

    const start = Math.max(1, current - 1);
    const end = Math.min(total - 2, current + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 3) pages.push('...');

    if (total > 1) pages.push(total - 1);

    return pages;
  }

  onPageSizeChange(event: Event) {
    const size = +(event.target as HTMLSelectElement).value;
    this.compPageSize = size;
    this.compCurrentPage = 0;
    this.pageSizeChange.emit(size);
    this.changePage.emit(0);
  }

  // Consumers either bind (onNext)/(onPrev) or only (changePage). Emit the
  // page change when nobody listens to the step events, so arrow buttons
  // work for both without fetching twice.
  next() {
    if (this.compCurrentPage < this.compTotalPage - 1) {
      this.compCurrentPage += 1;
      if (this.onNext.observed) this.onNext.emit();
      else this.changePage.emit(this.compCurrentPage);
    }
  }

  prev() {
    if (this.compCurrentPage > 0) {
      this.compCurrentPage -= 1;
      if (this.onPrev.observed) this.onPrev.emit();
      else this.changePage.emit(this.compCurrentPage);
    }
  }

  goToFirst() {
    if (this.compCurrentPage > 0) {
      this.compCurrentPage = 0;
      this.changePage.emit(0);
    }
  }

  goToLast() {
    const last = this.compTotalPage - 1;
    if (this.compCurrentPage < last) {
      this.compCurrentPage = last;
      this.changePage.emit(last);
    }
  }

  goToPage(page: number) {
    if (page !== this.compCurrentPage && page >= 0 && page < this.compTotalPage) {
      this.compCurrentPage = page;
      this.changePage.emit(page);
    }
  }

  goToPageDirect(input: HTMLInputElement) {
    const page = parseInt(input.value, 10) - 1;
    input.value = '';
    if (!isNaN(page) && page >= 0 && page < this.compTotalPage) {
      this.goToPage(page);
    }
  }

  ngOnChanges() {
    this.compCurrentPage = this.currentPage;
    this.compTotalPage = this.totalPages;
    if (this.pageSize && this.pageSizeOptions.includes(this.pageSize)) {
      this.compPageSize = this.pageSize;
    }
  }
}
